/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import $ from 'jquery';
import React, { Component, RefObject } from 'react';
import { OrderInformation } from 'src/adhoc/store/types/MetadataTypes';
import { ICrosstabHeaderPart, IInternalCrosstabHeaderCell, IInternalCrosstabHeaderRow, } from '../types/CrosstabVisualizationTypes';
import formattersFactory from '../../../../formatters/formattersFactory';
import { IAxisItem, TGroupedMeasures } from '../../../../store/types/StateTypes';
import { applyAdditionalLookAndFeelReact, obtainRenderVersion } from './CrosstabHeaderUtils';
import { ILinkToRefObjectPair } from '../../types/commonTypes';
import hyperlinkHelper from './hyperlinkHelper';
import i18n from '../../../../../i18n/AdHocBundle.properties';
import { designerPathEncoding } from './designerIntergrationHelper';

function formatValue(value: string, cellProperties: IAxisItem) {
    if (!value) {
        return '';
    }

    if (!cellProperties) {
        return value;
    }

    if (!cellProperties.type) {
        return value;
    }

    return formattersFactory(cellProperties.type).format(value, cellProperties.format, {
        categorizer: cellProperties.categorizer,
        useUserTimezone: true
    });
}

function pathSegment(dataCell: IInternalCrosstabHeaderCell, cellProperties: TGroupedMeasures) {
    let { label } = dataCell;
    if (dataCell.isTotal) {
        label = 'total_node';
    } else if (dataCell.isMeasure && cellProperties) {
        const measure = cellProperties.measures.find((measur: any) => (measur.id === dataCell.label)) as IAxisItem;
        if (measure) {
            label = measure.label; // eslint-disable-line prefer-destructuring
        }
    }

    // encodeUriComponent cannot be used here since space should not be encoded
    return designerPathEncoding(label);
}

type OnClickHandler = () => void;

export function cellFactory(cell: IInternalCrosstabHeaderCell, handleClick: OnClickHandler, cellProperties: IAxisItem | TGroupedMeasures) {
    /* eslint-disable react/jsx-closing-tag-location, no-nested-ternary */
    if (cell.loading) {
        return (
            <div
                className="jr jr-mDatatable-skeleton-cell-header"
                style={{
                    width: '50%',
                    top: '8px'
                }}
            >&nbsp;
            </div>
        )
    }

    let expander = null;
    if (cell.expandable) {
        let className = 'jr-mDatatable-cell-expander jr-mIcon jr jr-isCollapsed';
        if (cell.expanded) {
            className = 'jr-mDatatable-cell-expander jr-mIcon jr jr-isExpanded';
        }

        expander = (
            <span // eslint-disable-line jsx-a11y/control-has-associated-label
                role="button"
                tabIndex={0}
                className={className}
                onClick={handleClick}
            />
        );
    }

    let formattedValue = cell.label;
    if (cell.isMeasure && cellProperties.kind === 'measures') {
        const measure = (cellProperties as TGroupedMeasures).measures.find((measur: any) => (measur.id === cell.label)) as IAxisItem;
        if (measure) {
            formattedValue = measure.label;
        }
    } else if (cell.label === 'other_node') {
        formattedValue = i18n['adhoc.node.other.node'];
    } else if (cell.isTotal) {
        formattedValue = i18n['adhoc.node.total.node']
    } else {
        formattedValue = formatValue(cell.label, cellProperties as IAxisItem);
    }
    /* eslint-disable react/jsx-closing-tag-location */
    return (
        <div
            role="cell"
            className="jr jr-mDatatable-cell-text"
            onFocus={() => {}}
        >{expander} {formattedValue}</div>
    );
    /* eslint-enable react/jsx-closing-tag-location */
}

function sortingIconFactory(sorting: OrderInformation | undefined, nodes: IInternalCrosstabHeaderRow[], columnIndex: number, cellIndex: number) {
    if (!sorting || !sorting.members.length) {
        return null;
    }

    const nodeUri = nodes[columnIndex].nodes.reduce((memo, node, index) => {
        if (index <= cellIndex) {
            const dataCell = nodes[columnIndex].nodes[index];
            if (dataCell) {
                memo.push(dataCell.isTotal ? 'total_node' : (dataCell.label ? dataCell.label : ''));
            }
        }
        return memo;
    }, [] as string[]);

    const actualMember = sorting.members.find((member) => {
        if (member.path.length !== nodeUri.length) {
            // the amount of items in the member path and nodeUri must equal
            return false;
        }
        return member.path.reduce((memo: boolean, mPath: string, index: number) => (memo && mPath === nodeUri[index]), true);
    });

    if (!actualMember) {
        return null;
    }

    const sortingClassName = actualMember.ascending ? 'jr-sortAscending' : 'jr-sortDescending';
    return (
        <div
            className={`jr-mDatatable-cell-sort jr-mIcon jr-mIconSmall jr ${sortingClassName}`}
        />
    )
}

function topBottomFilterIconFactory(sorting: OrderInformation | undefined, nodes: IInternalCrosstabHeaderRow[], columnIndex: number, cellIndex: number) {
    if (!sorting || (!sorting.topN.length && !sorting.bottomN.length)) {
        return null;
    }

    const filterDirection = sorting.topN.length ? 'top' : 'bottom';
    const filtering = sorting.topN.length ? sorting.topN : sorting.bottomN;

    const nodeUri = nodes[columnIndex].nodes.reduce((memo, node, index) => {
        if (index <= cellIndex) {
            const dataCell = nodes[columnIndex].nodes[index];
            if (dataCell) {
                memo.push(dataCell.isTotal ? 'total_node' : (dataCell.label ? dataCell.label : ''));
            }
        }
        return memo;
    }, [] as string[]);

    const actualMember = filtering.find((member) => {
        if (member.path.length !== nodeUri.length) {
            // the amount of items in the member path and nodeUri must equal
            return false;
        }
        return member.path.reduce((memo: boolean, mPath: string, index: number) => (memo && mPath === nodeUri[index]), true);
    });

    if (!actualMember) {
        return null;
    }

    return (
        <span className={`jr-mDatatable-cell-filter ${filterDirection}`} />
    )
}

/* eslint-disable react/no-array-index-key */

export class CrosstabHeaderPart extends Component<ICrosstabHeaderPart, object> {

    headerRef: any

    hyperlinkEnabledFor: Array<string>

    constructor(props :any) {
        super(props);

        this.hyperlinkEnabledFor = [];
        this.hyperlinkEventListener = this.hyperlinkEventListener.bind(this);
    }

    componentDidMount() {
        const {
            linkOptions
        } = this.props;

        if (linkOptions && linkOptions.events && this.headerRef.current) {
            const $body :any = $(this.headerRef.current);
            Object.keys(linkOptions.events).forEach((eventName: string) => {
                this.hyperlinkEnabledFor.push(eventName);
                $body.on(eventName, '.jr-jHyperLink', this.hyperlinkEventListener);
            });
        }
    }

    componentWillUnmount() {
        const $body = $(this.headerRef.current);
        for (let i = 0; i < this.hyperlinkEnabledFor.length; i += 1) {
            $body.off(this.hyperlinkEnabledFor[i], '.jr-jHyperLink');
        }
    }

    onExpanderButtonClick(rowN: number, colN: number, isColumns: boolean) {
        const { nodes, onMemberExpansionChange } = this.props;

        const cell = nodes[rowN].nodes[colN];
        const memberExpansion = {
            path: nodes[rowN].nodes.slice(0, colN).map((c) => (c ? (c.isTotal ? 'All' : c.label) : '')),
            expanded: cell ? !cell.expanded : false
        };

        onMemberExpansionChange(memberExpansion, isColumns, rowN, colN);
    }

    hyperlinkEventListener(event: React.MouseEvent<HTMLDivElement>) {
        const {
            nodes,
            levelsStructure,
            linkOptions
        } = this.props;

        if (!linkOptions || !linkOptions.events) {
            return;
        }

        const callback = linkOptions.events[event.type];
        if (!callback) {
            return;
        }

        // don't react on click on "+" or "-" icons
        // @ts-ignore
        if (event.target.className && event.target.className.indexOf('jr-mIcon') !== -1) {
            return;
        }

        const cellData = event.currentTarget.dataset;
        const type = cellData.indexType || ''
        const index = +(cellData.localIndex || '-1');
        const depth = +(cellData.localDepth || '-1');

        const linkData = hyperlinkHelper.getHyperlinkDataFromNodes({
            levelsStructure,
            type,
            nodes: nodes[index].nodes,
            depth
        });

        const $Event = new $.Event((event as unknown) as string);

        callback.call(event.currentTarget, $Event, linkData);
    }

    renderRows() {
        const {
            headerWidths,
            nodes,
            levelsStructure,
            properties,
            levelsStructure: {
                rows, columns
            },
            linkOptions,
            hyperlinksCollector
        } = this.props;

        const collectCells = (linkOptions && linkOptions.beforeRender);
        const hyperlinksRefs :ILinkToRefObjectPair[] = [];
        const measures = rows.concat(columns).find((val) => (val.kind === 'measures')) as TGroupedMeasures;

        const headerRows = obtainRenderVersion(nodes, properties && properties.mergeCrosstabCells);

        if (!rows.length) {
            headerRows[0].isTotalRow = true;
            headerRows[0].nodes.push({
                loading: false,
                label: i18n['adhoc.node.rowgroup.node'],
                expandable: false,
                expanded: false,
                span: 1,
                isTotal: false,
                isMeasure: false,
            });
        }

        applyAdditionalLookAndFeelReact(headerRows, false);

        const trs = headerRows.map((row, rIndex) => {
            let rowClassName = 'jr-mDatatable-row jr';
            rowClassName += (rIndex + 1) % 2 === 0 ? ' jr-mDatatable-rowEven' : ' jr-mDatatable-rowOdd';
            rowClassName += row.isTotalRow ? ' jr-mDatatable-rowTotal' : '';
            rowClassName += row.rowGroup ? ' jr-mDatatable-rowGroup' : '';
            rowClassName += row.rowMember ? ' jr-mDatatable-rowMember' : '';
            rowClassName += row.rowMemberLast ? ' jr-mDatatable-rowMemberLast' : '';
            rowClassName += row.rowSingleMember ? ' jr-mDatatable-rowGroupSinglemember' : '';
            const cells = row.nodes;

            return (
                <tr key={row.index} data-index={row.index} className={rowClassName}>
                    {
                        cells.map((cell, cIndex) => {
                            if (!cell) {
                                return null;
                            }
                            const cellProperties = rows[cIndex] as IAxisItem;
                            const cellClassNames = ['jr-mDatatable-cell', 'jr-mDatatable-cellRowlabel', 'jr-mDatatable-cell-hyperlink', 'jr'];
                            if (cell.isTotal) {
                                if (cell.isMeasure) {
                                    cellClassNames.push('jr-mDatatable-cellTotalMeasure');
                                    cellClassNames.push('jr-mDatatable-cellTotal');
                                }
                                if (nodes[rIndex].nodes[cIndex] && !nodes[rIndex].nodes[cIndex]!.isTotal) {
                                    cell.isTotal = false; // eslint-disable-line no-param-reassign
                                } else {
                                    cellClassNames.push('jr-mDatatable-cellTotal');
                                }
                            } else {
                                cellClassNames.push(cell.isMeasure ? 'jr-mDatatable-cellMeasure' : 'jr-mDatatable-cellField');
                            }

                            if (linkOptions) {
                                cellClassNames.push('jr-jHyperLink', 'jr-jHyperLink-rowHeader');
                            }

                            if (cell.loading) {
                                cellClassNames.push('jr-mDatatable-skeleton-cell');
                            }

                            // we need to give to beforeRender() callback the DOM node of the cell,
                            // and to do this we need a 'ref' object.
                            const ref: RefObject<any> = React.createRef();
                            // and we also don't need to actually do anything if user didn't provide
                            // the beforeRender callback
                            if (!cell.loading && collectCells) {
                                const linkData = hyperlinkHelper.getHyperlinkDataFromNodes({
                                    levelsStructure,
                                    type: 'row',
                                    nodes: nodes[rIndex].nodes,
                                    depth: cIndex
                                });
                                hyperlinksRefs.push({
                                    data: linkData,
                                    ref
                                });
                            }

                            const cellStyles :any = {};

                            if (headerWidths[cIndex]) {
                                cellStyles.minWidth = headerWidths[cIndex];
                            }

                            const nodeUri = nodes[rIndex].nodes.reduce((memo, node, index) => {
                                if (index <= cIndex) {
                                    const dataCell = nodes[rIndex].nodes[index]!;
                                    if (dataCell) {
                                        memo.push(pathSegment(dataCell, measures));
                                    }
                                }

                                return memo;
                            }, ['']);

                            return (
                                <td
                                    ref={ref}
                                    key={`${row.index}_${cIndex}`}
                                    className={cellClassNames.join(' ')}
                                    title={cell.label}
                                    style={cellStyles}
                                    rowSpan={cell.span ? cell.span : undefined}
                                    data-index-type="row"
                                    data-local-index={rIndex}
                                    data-local-depth={cIndex}
                                    data-uri={nodeUri.join('/')} // this property required for automation tests
                                >
                                    <div className="jr-mDatatable-cell-wrapper jr">
                                        {cellFactory(cell, () => this.onExpanderButtonClick(rIndex, cIndex, false), cellProperties)}
                                    </div>
                                </td>
                            )
                        })
                    }
                </tr>
            )
        });

        if (collectCells) {
            hyperlinksCollector('rows', hyperlinksRefs);
        }

        return (
            <tbody className="jr-mDatatable-body jr">
                {trs}
            </tbody>
        )
    }

    renderCols() {
        const {
            headerWidths,
            nodes,
            levelsStructure,
            properties,
            levelsStructure: {
                columns, rows
            },
            offset,
            linkOptions,
            hyperlinksCollector,
            sorting
        } = this.props;

        const measures = rows.concat(columns).find((val) => (val.kind === 'measures')) as TGroupedMeasures;
        const collectCells = (linkOptions && linkOptions.beforeRender);
        const hyperlinksRefs :ILinkToRefObjectPair[] = [];
        const headerColumns = obtainRenderVersion(nodes, properties && properties.mergeCrosstabCells);

        applyAdditionalLookAndFeelReact(headerColumns, true);

        const firstColumn = headerColumns[0];
        const firstColumnHeaderCells = (firstColumn && firstColumn.nodes) || [];

        const trs = firstColumnHeaderCells.map((__, cellIndex) => (
            <tr key={cellIndex} className="jr-mDatatable-row jr">
                {
                    headerColumns.map((column, columnIndex) => {
                        const cell = column.nodes[cellIndex];
                        if (!cell) {
                            return null;
                        }
                        const cellProperties = columns[cellIndex] as IAxisItem;

                        const cellClassNames = [
                            'jr-mDatatable-cell',
                            'jr-mDatatable-cellColumnlabel',
                            'jr-mDatatable-cell-hyperlink',
                            'jr'
                        ];
                        // eslint-disable-next-line no-nested-ternary
                        if (cell.isTotal) {
                            cellClassNames.push('jr-mDatatable-cellTotal');
                            if (cell.isMeasure) {
                                cellClassNames.push('jr-mDatatable-cellTotalMeasure');
                            }
                        } else {
                            cellClassNames.push(cell.isMeasure ? 'jr-mDatatable-cellMeasure' : 'jr-mDatatable-cellField');
                        }

                        if (linkOptions) {
                            cellClassNames.push('jr-jHyperLink', 'jr-jHyperLink-columnHeader');
                        }

                        if (cell.loading) {
                            cellClassNames.push('jr-mDatatable-skeleton-cell');
                        }

                        // we need to give to beforeRender() callback the DOM node of the cell,
                        // and to do this we need a 'ref' object.
                        const ref: RefObject<any> = React.createRef();
                        // and we also don't need to actually do anything if user didn't provide
                        // the beforeRender callback
                        if (!cell.loading && collectCells) {
                            const linkData = hyperlinkHelper.getHyperlinkDataFromNodes({
                                levelsStructure,
                                type: 'column',
                                nodes: nodes[columnIndex].nodes,
                                depth: cellIndex
                            });
                            hyperlinksRefs.push({
                                data: linkData,
                                ref
                            });
                        }

                        const nodeUri = nodes[columnIndex].nodes.reduce((memo, node, index) => {
                            if (index <= cellIndex) {
                                const dataCell = nodes[columnIndex].nodes[index];
                                if (dataCell) {
                                    memo.push(pathSegment(dataCell, measures));
                                }
                            }

                            return memo;
                        }, ['']);

                        return (
                            <th
                                ref={ref}
                                key={`${column.index}_${columnIndex}`}
                                className={cellClassNames.join(' ')}
                                title={cell.label}
                                colSpan={cell.span ? cell.span : undefined}
                                data-index-type="column"
                                data-local-index={columnIndex}
                                data-local-depth={cellIndex}
                                data-uri={nodeUri.join('/')} // this property requred for automation tests
                            >
                                <div className="jr-mDatatable-cell-wrapper jr">
                                    {cellFactory(cell, () => this.onExpanderButtonClick(columnIndex, cellIndex, true), cellProperties)}
                                    {sortingIconFactory(sorting, nodes, columnIndex, cellIndex)}
                                    {topBottomFilterIconFactory(sorting, nodes, columnIndex, cellIndex)}
                                </div>
                            </th>
                        )
                    })
                }
            </tr>
        ));

        if (collectCells) {
            hyperlinksCollector('columns', hyperlinksRefs);
        }

        return (
            <thead className="jr-mDatatable-header jr" style={{ left: offset }}>
                {trs}
                <tr className="jr-mDatatable-row jr">
                    {headerColumns.map((column) => (
                        <th
                            style={{ minWidth: headerWidths[column.index] }}
                            data-local-index={column.index}
                            key={column.index}
                            className="jr-mDatatable-cell jr-mDatatable-cellSpacer jr jr-mDatatable-cellField"
                        >
                            <div className="jr-mDatatable-cell-wrapper jr">
                                <div className="jr-mDatatable-cell-text jr" />
                            </div>
                        </th>
                    ))}
                </tr>
            </thead>
        )
    }

    render() {
        const { columnsHeader: isColumnsHeader, offset } = this.props;
        this.headerRef = React.createRef();

        return (
            <table
                ref={this.headerRef}
                className="jr-mDatatable jr-mDatatableCrosstab jr"
                style={{ position: 'relative', [isColumnsHeader ? 'left' : 'top']: offset }}
            >
                {isColumnsHeader ? this.renderCols() : this.renderRows()}
            </table>
        )
    }
}
